/* eslint-disable use-isnan */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ReactECharts from "echarts-for-react";
import moment from "moment";
import useMeasure from "react-use-measure";
import { useTheme } from "@mui/styles";
import {
  NotificationsActiveOutlined,
  NotificationsOffOutlined,
  UpdateDisabledOutlined,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { app, socketApp } from "../../services";
import {
  buildLatestEventParams,
  normalizeCachedSnapshot,
  normalizeLatestEventSnapshot,
} from "./dataUtils";
import { getGaugeLabelFontSize } from "../ItemDashboard/chartUtils";

import "./itemDashboardFavorites.css";

const colorsArray = [
  "#80FFA5",
  "#00DDFF",
  "#37A2FF",
  "#FF0087",
  "#FFBF00",
  "#80FFA5",
  "#00DDFF",
  "#37A2FF",
  "#FF0087",
  "#FFBF00",
  "#80FFA5",
  "#00DDFF",
  "#37A2FF",
  "#FF0087",
  "#FFBF00",
  "#80FFA5",
  "#00DDFF",
  "#37A2FF",
  "#FF0087",
  "#FFBF00",
];

const EMPTY_MESSAGES = {
  loading: "Cargando",
  noMeasure: "Sin medida",
  noData: "Sin datos",
  noValue: "Sin valor",
};

const hasGaugeValue = (serie) => {
  const value = serie?.gauge?.value;
  return value !== null && value !== undefined && Number.isFinite(Number(value));
};

const formatGaugeValue = (value) =>
  (Number(value).toFixed(2) * 1).toLocaleString("es-MX");

const formatGaugeUnit = (serie) => {
  const unit = serie?.measure?.medida?.vc_abreviatura;
  return unit && unit !== " " ? ` ${unit}` : "";
};

function ItemDashboardFavorites({
  selected,
  defaultMeasure,
  lite = false,
  alarms = [],
  stopData,
}) {
  const token = useSelector((state) => state.session.token);
  const activeApplication = useSelector((state) => state.session.activeApplication) || "tgone";

  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.up("sm"), { defaultMatches: true });

  const [loadingData, setLoadignData] = useState({ loading: false, error: null });
  const [disableAlert, setDisableAlert] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bat, setBat] = useState(null);

  const [refChart, boundInner] = useMeasure();

  const [typesMeasures, setTypesMeasures] = useState(
    defaultMeasure ? [defaultMeasure] : []
  );
  const [measureSelect, setMeasureSelect] = useState();
  const [measuresSelected, setMeasuresSelected] = useState(
    defaultMeasure ? [defaultMeasure] : []
  );
  const [customConfig, setCustomConfig] = useState({ options: {}, series: [] });
  const [emptyMessage, setEmptyMessage] = useState(EMPTY_MESSAGES.loading);

  // ---- refs para evitar memory leaks ----
  const mountedRef = useRef(false);
  const abortRef = useRef(null); // AbortController
  const selectedRef = useRef(null);
  const measuresSelectedRef = useRef([]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  useEffect(() => {
    selectedRef.current = selected;
  }, [selected]);

  useEffect(() => {
    measuresSelectedRef.current = measuresSelected;
  }, [measuresSelected]);

  const safeSet = (setter) => {
    if (!mountedRef.current) return;
    setter();
  };

  // ---- helpers ----
  const getDataRetry = useCallback(async (selectedId) => {
    let response = null;
    const requestSelectedId = selectedId || selectedRef.current?.id;

    if (!requestSelectedId) return null;

    // cancela fetch previo si existe
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      safeSet(() => setLoadignData({ loading: true, error: null }));

      const baseURL = process.env.REACT_APP_API_URL || "http://localhost:3030";
      const url = `${baseURL}/devices-sections-measures/${requestSelectedId}?application=${activeApplication}`;

      const res = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        signal: controller.signal,
      });

      if (res?.ok) {
        const cachedResponse = await res.json();
        response = normalizeCachedSnapshot(cachedResponse, measuresSelectedRef.current);
      }

      // Algunos dispositivos tienen historial pero todavía no cuentan con una
      // fila utilizable en devices_sections_measures. En ese caso usamos el
      // mismo origen que el preview para no dejar vacío el favorito.
      if (!response) {
        const params = buildLatestEventParams(
          requestSelectedId,
          measuresSelectedRef.current,
          typesMeasures.some((measure) => measure?.vc_fieldName === "BAT")
        );
        const eventsUrl = `${baseURL}/events?dataString=${encodeURIComponent(JSON.stringify(params))}`;
        const eventsResponse = await fetch(eventsUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        });

        if (eventsResponse?.ok) {
          response = normalizeLatestEventSnapshot(await eventsResponse.json());
        } else {
          console.error(
            "Error al consultar la última medición:",
            eventsResponse?.status,
            eventsResponse?.statusText
          );
        }
      }

      safeSet(() => setLoadignData({ loading: false, error: null }));
    } catch (error) {
      // si fue abort, no es error real
      if (error?.name === "AbortError") return null;

      safeSet(() => setLoadignData({ loading: false, error }));
      console.log("error", error);
    }

    return response;
  }, [token, activeApplication, typesMeasures]);

  const getTypes = useCallback(async () => {
    try {
      const s = selectedRef.current;
      if (!s) return [];
      const requestSelectedId = s.id;

      const types = await app.service("custom-devices-sections").find({ query: s });
      if (selectedRef.current?.id !== requestSelectedId) return [];

      const rows = types?.rows || [];
      const defaultMeasures = rows.filter((e) => e.bl_default);

      safeSet(() => {
        setTypesMeasures(rows);
        if (!measureSelect) {
          setMeasuresSelected(defaultMeasures);
          if (!defaultMeasures.length) {
            setEmptyMessage(EMPTY_MESSAGES.noMeasure);
            setLoading(false);
          }
        }
      });

      return !measureSelect ? defaultMeasures : measuresSelectedRef.current;
    } catch (e) {
      console.log("getTypes error", e);
      safeSet(() => {
        setEmptyMessage(EMPTY_MESSAGES.noMeasure);
        setLoading(false);
      });
      return [];
    }
  }, [measureSelect]);

  const getEvents = useCallback(
    async (animationLoad = true) => {
      const s = selectedRef.current;
      const selectedMeasures = measuresSelectedRef.current;

      if (!s) return;
      const requestSelectedId = s.id;

      if (animationLoad) {
        safeSet(() => {
          setEmptyMessage(EMPTY_MESSAGES.loading);
          setCustomConfig((prev) => ({ ...prev, options: null, series: [] }));
          setLoading(true);
        });
      }

      if (!selectedMeasures?.length) {
        const defaultMeasures = await getTypes();
        if (selectedRef.current?.id !== requestSelectedId) return;
        if (!defaultMeasures?.length) {
          safeSet(() => {
            setEmptyMessage(EMPTY_MESSAGES.noMeasure);
            setCustomConfig((prev) => ({ ...prev, series: [] }));
            setLoading(false);
          });
        }
        return;
      }

      const response = await getDataRetry(requestSelectedId);
      if (selectedRef.current?.id !== requestSelectedId) return;

      const jsonData = Array.isArray(response?.json_data) ? response.json_data : null;
      if (!jsonData) {
        console.log("response error", response);
        safeSet(() => {
          setEmptyMessage(EMPTY_MESSAGES.noData);
          setCustomConfig((prev) => ({
            ...prev,
            gauge: {
              min: { value: 0, type: "empty" },
              max: { value: 1, type: "empty" },
            },
            series: [],
          }));
          setLoading(false);
        });
        return;
      }

      const batValue =
        jsonData.find((e) => e.vc_field === "BAT")?.nu_value ?? null;

      const series = [];
      const categories = [moment(response?.createdAt).format("DD-MM-YY HH:mm")];

      let lastObj = null;
      selectedMeasures.forEach((meas, indexMeas) => {
        const data = [];
        let media = { average: 0, total: 0, length: 0 };
        let min = null;
        let max = null;
        let last = null;

        const item = jsonData
          .slice()
          .sort((a, b) => moment(b.createdAt) - moment(a.createdAt))
          .find((e) => e.vc_field === meas.vc_fieldName);

        if (item?.nu_value !== null && item?.nu_value !== undefined) {
          const val = Number(item.nu_value);
          if (Number.isFinite(val)) {
            lastObj = { ...item, createdAt: response.createdAt };
            last = val;

            data.push([
              moment(response.createdAt).format("DD-MM-YY HH:mm:ss"),
              val,
              response.createdAt,
            ]);

            if (max === null || val >= max) max = val;
            if (min === null || val <= min) min = val;

            const mediaEdit = {
              average: ((media.total + val) / (media.length + 1)).toFixed(2),
              total: media.total + val,
              length: media.length + 1,
            };
            media = mediaEdit;
          }
        }

        series.push({
          name: meas?.vc_nombre,
          data,
          measure: meas,
          color: colorsArray[indexMeas],
          min: meas.nu_min ?? null,
          max: meas.nu_max ?? null,
          gauge: {
            color: colorsArray[indexMeas],
            value: last,
            max,
            min,
            ...media,
          },
        });
      });

      const gauge = {};
      const minRange =
        [...series].map((e) => e.min).filter((v) => v !== null)
          .sort((a, b) => a - b)[0] ?? null;

      gauge.min = minRange !== null
        ? { value: minRange, type: "range" }
        : {
            value:
              [...series].map((e) => e.gauge?.min).filter((v) => v !== null)
                .sort((a, b) => a - b)[0] ?? 0,
            type: "measures",
          };

      const maxRange =
        [...series].map((e) => e.max).filter((v) => v !== null)
          .sort((a, b) => b - a)[0] ?? null;

      gauge.max = maxRange !== null
        ? { value: maxRange, type: "range" }
        : {
            value:
              [...series].map((e) => e.gauge?.max).filter((v) => v !== null)
                .sort((a, b) => b - a)[0] ?? 1,
            type: "measures",
          };

      safeSet(() => {
        setBat(batValue);
        setLoading(false);
        setEmptyMessage(series.some(hasGaugeValue) ? "" : EMPTY_MESSAGES.noValue);
        setCustomConfig((prev) => ({
          ...prev,
          lastObj,
          gauge,
          options: {
            ...(prev.options || {}),
            xaxis: { type: "datetime", categories },
          },
          series,
        }));
      });
    },
    [getDataRetry, getTypes]
  );

  // ---- subscribe/unsubscribe (si tu servicio se llama "suscribe") ----
  const buildChannelPayload = useCallback((value) => {
    if (Array.isArray(value)) return { channels: value };
    return { channel: value };
  }, []);

  const suscribe = useCallback(async () => {
    try {
      const s = selectedRef.current;
      if (!s?.id) return;

      const res = await socketApp.service("suscribe").create(buildChannelPayload(s.id));
      console.log("[suscribe] ok", res);
    } catch (e) {
      console.log("[suscribe] error", e);
    }
  }, [buildChannelPayload]);

  const unsuscribe = useCallback(async () => {
    try {
      const s = selectedRef.current;
      if (!s?.id) return;

      const res = await socketApp.service("suscribe").remove(null, { query: buildChannelPayload(s.id) });
      console.log("[unsuscribe] ok", res);
    } catch (e) {
      console.log("[unsuscribe] error", e);
    }
  }, [buildChannelPayload]);

  // ---- effects ----
  useEffect(() => {
    if (!stopData) suscribe();
  }, [stopData, suscribe]);

  useEffect(() => {
    if (!!selected?.id && !!measuresSelected.length && !stopData) {
      getEvents(true);
    }
  }, [selected?.id, measuresSelected.length, stopData, getEvents]);

  // Eventos realtime (NO uses removeAllListeners)
  useEffect(() => {
    const events = socketApp.service("events");

    const onCreated = () => getEvents(true);
    const onUpdated = () => getEvents(true);

    suscribe();

    events.on("created", onCreated);
    events.on("updated", onUpdated);

    return () => {
      events.off("created", onCreated);
      events.off("updated", onUpdated);
      unsuscribe();
    };
  }, [getEvents, suscribe, unsuscribe]);

  useEffect(() => {
    getTypes();
    if (!defaultMeasure) {
      safeSet(() => {
        setEmptyMessage(EMPTY_MESSAGES.loading);
        setCustomConfig((prev) => ({ ...prev, series: [] }));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const colorsAlarms = useMemo(() => {
    return (customConfig?.series || []).map((serie, index) => {
      const alarmDevice = alarms.filter(
        (e) =>
          e.medida?.vc_fieldName === serie?.measure?.vc_fieldName &&
          (serie?.gauge?.value > e.nu_max || serie?.gauge?.value < e.nu_min)
      );
      if (alarmDevice.length > 0) return "red";

      return [
        (customConfig.series?.[0] || { color: "#00FFBB" }).gauge?.color,
        "#FFBB00",
        "#11FFAA",
        "#11FFFF",
      ][index];
    });
  }, [customConfig?.series, alarms]);

  const series = customConfig?.series || [];
  const gaugeSeriesCount = series.length;
  const denseGaugeLabels = gaugeSeriesCount >= 5;
  const safeGaugeWidth = boundInner.width || 320;
  const showEmptyMessage = !series.length;
  const centerMessage =
    emptyMessage || (loading ? EMPTY_MESSAGES.loading : EMPTY_MESSAGES.noData);

  return [
    <div
      ref={refChart}
      key={"gauge"}
      style={{ width: "100%", height: boundInner.width, position: "relative" }}
    >
      <Paper
        className={!disableAlert && colorsAlarms.includes("red") ? "alertBox" : undefined}
        style={{
          display: "flex",
          borderRadius: 10,
          height: "100%",
          width: "100%",
          overflow: "hidden",
          border: "1px solid rgba(200,200,200,0.3)",
        }}
      >
        <ReactECharts
          style={{ width: boundInner.width, height: boundInner.width }}
          option={{
            animationDuration: 450,
            animationDurationUpdate: 250,
            animationEasing: "cubicOut",
            animationEasingUpdate: "cubicOut",
            color: colorsArray || colorsAlarms,
            backgroundColor: "transparent",
            series: [
              {
                type: "gauge",
                min: (customConfig.gauge?.min?.value ?? 0) * 0.9,
                max: (customConfig.gauge?.max?.value ?? 1) * 1.1,
                startAngle: 90,
                endAngle: -270,
                pointer: { show: false },
                progress: {
                  show: true,
                  overlap: false,
                  roundCap: true,
                  clip: false,
                  itemStyle: {
                    width: 10,
                    borderWidth: 1,
                    borderColor: "rgba(0,0,0,0.2)",
                  },
                },
                axisLine: { lineStyle: { width: 20 } },
                splitLine: { show: false },
                axisTick: { show: false },
                axisLabel: { show: false },
                data: series.map((serie) => ({
                  value:
                    hasGaugeValue(serie)
                      ? Number(serie.gauge.value).toFixed(2) * 1
                      : null,
                  name: serie.name,
                  title: { show: false },
                  detail: { show: false },
                })),
                detail: {
                  formatter: (e) => {
                    const u = (customConfig.series?.[0] || {})?.measure?.medida?.vc_abreviatura;
                    return `${Number(e).toFixed(2)}${u && u !== " " ? " " + u : ""}`;
                  },
                },
              },
            ],
          }}
          notMerge={false}
          lazyUpdate={false}
          theme={"dark"}
          opts={{}}
          loadingOption={{ show: true }}
        />

        <Box
          width={"100%"}
          position="absolute"
          flexDirection={"column"}
          justifyContent={"center"}
          display="flex"
          left={0}
          top={0}
          height={"100%"}
          p={0}
        >
          <Box
            width={"100%"}
            height={"100%"}
            position="relative"
            justifyContent={"center"}
            display="flex"
          >
            <Box
              position="relative"
              alignSelf={"center"}
              flexDirection={series.length > 1 ? "column" : "row"}
              justifyContent={"center"}
              display="flex"
              style={{
                width: gaugeSeriesCount === 1 ? "68%" : denseGaugeLabels ? "62%" : "58%",
                maxHeight: denseGaugeLabels ? "70%" : "58%",
                maxWidth: gaugeSeriesCount === 1 ? "68%" : denseGaugeLabels ? "62%" : "58%",
                overflow: "visible",
                boxSizing: "border-box",
                padding: denseGaugeLabels ? 1 : 2,
              }}
            >
              {showEmptyMessage ? (
                <Box
                  className="container-text"
                  style={{
                    textAlign: "center",
                    height: "100%",
                    flexDirection: "row",
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    overflow: "hidden",
                    boxSizing: "border-box",
                  }}
                >
                  <Typography
                    className="text"
                    color="text.secondary"
                    fontWeight={700}
                    style={{
                      fontSize: isMd
                        ? "clamp(0.7rem, 1vw, 1rem)"
                        : "clamp(0.75rem, 3.8vw, 1rem)",
                      lineHeight: 1.15,
                      whiteSpace: "pre-line",
                    }}
                  >
                    {centerMessage}
                  </Typography>
                </Box>
              ) : series.map((serie, index) => {
                const gaugeLabel = hasGaugeValue(serie)
                  ? `${formatGaugeValue(serie.gauge.value)}${formatGaugeUnit(serie)}`
                  : EMPTY_MESSAGES.noValue;
                const labelFontSize = getGaugeLabelFontSize({
                  gaugeWidth: safeGaugeWidth,
                  label: gaugeLabel,
                  seriesCount: gaugeSeriesCount,
                });

                return (
                  <Box
                    key={`${serie.name}-${index}`}
                    style={{
                      alignItems: "center",
                      display: "flex",
                      flex: "1 1 auto",
                      justifyContent: "center",
                      minHeight: 0,
                      minWidth: 0,
                      padding: denseGaugeLabels ? "1px 0" : 1,
                      textAlign: "center",
                      width: "100%",
                    }}
                  >
                    <Typography
                      component="span"
                      color={serie?.color}
                      fontWeight={700}
                      style={{
                        alignItems: "center",
                        border: "none",
                        borderBottom: "none",
                        borderRadius: 0,
                        boxSizing: "border-box",
                        display: "inline-flex",
                        fontSize: labelFontSize,
                        height: "auto",
                        justifyContent: "center",
                        lineHeight: 1.08,
                        maxWidth: "100%",
                        overflow: "hidden",
                        padding: denseGaugeLabels ? "1px 3px" : "1px 4px",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "fit-content",
                      }}
                    >
                      {gaugeLabel}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        {colorsAlarms.includes("red") && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setDisableAlert((v) => !v);
            }}
            size="small"
            style={{ position: "absolute", top: "0.5vw", right: "0.5vw" }}
          >
            {disableAlert ? <NotificationsOffOutlined /> : <NotificationsActiveOutlined className="alert" />}
          </IconButton>
        )}

        {(!!loadingData.error || !!loadingData.loading) && (
          <Button
            onClick={() => getEvents(true)}
            fullWidth
            style={{
              height: "100%",
              position: "absolute",
              top: 0,
              left: 0,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            {!!loadingData.loading ? (
              <>
                <CircularProgress size={24} />
                <Typography color="text.secondary" fontWeight={700} fontSize="0.85rem">
                  {EMPTY_MESSAGES.loading}
                </Typography>
              </>
            ) : (
              <UpdateDisabledOutlined />
            )}
          </Button>
        )}
      </Paper>
    </div>,
  ];
}

export default ItemDashboardFavorites;
